import { useState } from 'react';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Switch from '@ui/switch/switch';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { getDestinationDisplayNameMapping } from './destination-type-options';

type DestinationCardProps = {
  localId: string;
  name: string;
  icon: string;
  url: string;
  streamKey: string;
  selected: boolean;
  isExpanded: boolean;
  canDelete: boolean;
  readOnly?: boolean;
  onToggleExpand: () => void;
  onToggleSelected: (localId: string, value: string, on: boolean) => void;
  editableName?: boolean;
  onFieldChange: (localId: string, field: 'url' | 'streamKey' | 'name', value: string) => void;
  onDelete: (localId: string) => void;
  onUrlBlur?: (localId: string) => void;
  urlTouched?: boolean;
};

const isValidRtmpUrl = (value: string) => /^rtmps?:\/\/.+/.test(value);

const DestinationCard = ({
  localId,
  name,
  icon,
  url,
  streamKey,
  selected,
  isExpanded,
  canDelete,
  readOnly,
  editableName,
  onToggleExpand,
  onToggleSelected,
  onFieldChange,
  onDelete,
  onUrlBlur,
  urlTouched,
}: DestinationCardProps) => {
  const [streamKeyVisible, setStreamKeyVisible] = useState(false);

  const showUrlError = !readOnly && !!urlTouched && !!url && !isValidRtmpUrl(url);

  return (
    <Container design="GlobalCont2" borderRadius="8px" className="overflow-hidden">
      <Container className="flex ai-center jc-space-between padding-h-16 padding-v-22">
        <Container className="flex ai-center gap-12 flex-1">
          <Container
            width="40px"
            height="36px"
            className="flex ai-center jc-center radius-8 flex-shrink-0"
            background="var(--global-cont-3)"
          >
            <Icon name={icon} size={24} color="var(--global-ico-text-3)" />
          </Container>
          {editableName ? (
            <InputWithOverlay
              isBorderless
              background="transparent"
              inputHeight="32px"
              wrapperClass="width-100-percent"
              inputProps={{
                className: 'radius-8 padding-0',
                maxLength: 50,
                placeholder: 'Destination name',
                value: name,
                onChange: (e) => onFieldChange(localId, 'name', e.target.value),
              }}
            />
          ) : (
            <Text className="S3" design="GlobalIcoText1" clamp={1}>
              {getDestinationDisplayNameMapping(name)}
            </Text>
          )}
        </Container>

        <Container className="flex ai-center gap-12">
          <Button
            className="clear-button padding-2"
            borderRadius="100%"
            design="LocalBttnSecTransp"
            onClick={onToggleExpand}
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            <Icon
              name={ICONS.SHOW_EYE}
              size={16}
              color={isExpanded ? 'var(--global-ico-text-1)' : 'var(--global-ico-text-3)'}
            />
          </Button>

          {canDelete && (
            <Button
              className="clear-button"
              borderRadius="100%"
              design="LocalBttnSecTransp"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(localId);
              }}
            >
              <Icon name={ICONS.DELETE_TRASH_BIN} size={16} color="var(--global-ico-text-3)" />
            </Button>
          )}

          <Switch value={localId} on={selected} onClick={(val, on) => onToggleSelected(localId, val, on)} />
        </Container>
      </Container>

      {isExpanded && (
        <Container className="flex fd-column gap-12 padding-h-16 padding-b-16">
          <div>
            <Text className="C2 padding-b-6" design="GlobalIcoText2">
              RTMP Address
            </Text>
            <InputWithOverlay
              isBorderless
              background="var(--global-cont-3)"
              status={showUrlError ? 'error' : undefined}
              inputHeight="32px"
              innerContentStyle={{ right: 0 }}
              inputProps={{
                className: 'radius-8',
                placeholder: 'rtmp://...',
                value: url,
                readOnly,
                autoComplete: 'off',
                spellCheck: false,
                style: { color: readOnly ? 'var(--global-ico-text-4)' : 'var(--global-ico-text-1)' },
                onChange: (e) => onFieldChange(localId, 'url', e.target.value),
                onBlur: () => onUrlBlur?.(localId),
              }}
              renderInnerContent={() => (
                <Container className="padding-h-10">
                  <Button
                    className="clear-button padding-2"
                    borderRadius="100%"
                    design="LocalBttnSecTransp"
                    onClick={() => navigator.clipboard.writeText(url)}
                    aria-label="Copy RTMP address"
                  >
                    <Icon name={ICONS.COPY} size={14} color="var(--global-ico-text-2)" />
                  </Button>
                </Container>
              )}
            />
          </div>
          <div>
            <Text className="C2 padding-b-6" design="GlobalIcoText2">
              Stream Key
            </Text>
            <InputWithOverlay
              isBorderless
              background="var(--global-cont-3)"
              inputHeight="32px"
              innerContentStyle={{ right: 0 }}
              inputProps={{
                className: 'radius-8',
                placeholder: 'Stream key',
                type: streamKeyVisible ? 'text' : 'password',
                value: streamKey,
                readOnly,
                autoComplete: 'off',
                spellCheck: false,
                style: { color: readOnly ? 'var(--global-ico-text-4)' : 'var(--global-ico-text-1)' },
                onChange: (e) => onFieldChange(localId, 'streamKey', e.target.value),
              }}
              renderInnerContent={() => (
                <Container className="flex ai-center padding-h-10 gap-10">
                  <Button
                    className="clear-button padding-2"
                    borderRadius="100%"
                    onClick={() => setStreamKeyVisible((v) => !v)}
                    design="LocalBttnSecTransp"
                    aria-label={streamKeyVisible ? 'Hide stream key' : 'Show stream key'}
                  >
                    <Icon
                      name={streamKeyVisible ? ICONS.VIEWERS_EYE_OFF : ICONS.SHOW_EYE}
                      size={14}
                      color="var(--global-ico-text-2)"
                    />
                  </Button>
                  <Button
                    className="clear-button padding-2"
                    borderRadius="100%"
                    design="LocalBttnSecTransp"
                    onClick={() => navigator.clipboard.writeText(streamKey)}
                    aria-label="Copy stream key"
                  >
                    <Icon name={ICONS.COPY} size={14} color="var(--global-ico-text-2)" />
                  </Button>
                </Container>
              )}
            />
          </div>
        </Container>
      )}
    </Container>
  );
};

export default DestinationCard;
